import { DAYS } from "../constants";
import { Location } from "../types";
import { isoDateToLocalDate, sortDaysByWeek } from "../utils";
import type { QuickRescheduleOption } from "../api/location/api";

export const quickRescheduleKindLabel = (kind: QuickRescheduleOption["kind"]): string =>
  kind === "today"
    ? "Today"
    : kind === "followingCadence"
      ? "Following cadence"
      : "Next cadence";

export function formatQuickCadenceDate(dateIso: string) {
  const date = isoDateToLocalDate(dateIso);
  const day = date.getDate();
  const suffix =
    day >= 11 && day <= 13
      ? "th"
      : day % 10 === 1
        ? "st"
        : day % 10 === 2
          ? "nd"
          : day % 10 === 3
            ? "rd"
            : "th";
  return `${date.toLocaleString(undefined, { month: "long" })} ${day}${suffix}`;
}

const PRESET_TO_DAYS: Record<string, number> = {
  daily: 1,
  weekly: 7,
  bimonthly: 14,
  monthly: 30,
};

const PRACTICAL_DAYS_LABELS: Record<number, string> = {
  21: "Every 3 weeks",
  28: "Every 4 weeks",
  42: "Every 6 weeks",
  60: "Every 2 months",
  90: "Every 3 months",
};

export const CADENCE_MIN_DAYS = 1;
export const CADENCE_MAX_DAYS = 365;

export const CADENCE_FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "bimonthly", label: "Biweekly" },
  { value: "monthly", label: "Monthly" },
  { value: "other", label: "Custom" },
];

export const cadenceFrequencyLabel = (frequency?: string): string =>
  CADENCE_FREQUENCY_OPTIONS.find((option) => option.value === frequency)?.label ||
  "";

export const cadenceDaysLabel = (days: number): string => {
  const frequency = resolveFrequency(undefined, days);
  if (frequency === "other" && PRACTICAL_DAYS_LABELS[days]) {
    return PRACTICAL_DAYS_LABELS[days];
  }
  return frequency === "other"
    ? `Every ${days} days`
    : cadenceFrequencyLabel(frequency);
};

export const cadenceSuggestionFrequencyText = (
  days: number,
  reason?: string,
): string => {
  const label = cadenceDaysLabel(days);
  return reason ? `${label}. ${reason}` : label;
};

const DAYS_TO_PRESET: Record<number, string> = Object.entries(
  PRESET_TO_DAYS
).reduce(
  (acc, [key, val]) => ({ ...acc, [val]: key }),
  {} as Record<number, string>
);

export function parseDaysBetween(val?: unknown): number | undefined {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    if (!val.trim()) return undefined;
    const n = Number(val);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

export function isValidCadenceDays(val?: unknown): boolean {
  const days = parseDaysBetween(val);
  return (
    typeof days === "number" &&
    Number.isInteger(days) &&
    days >= CADENCE_MIN_DAYS &&
    days <= CADENCE_MAX_DAYS
  );
}

export function resolveFrequency(
  freqRaw: Location["serviceCadence"] extends infer T
    ? T extends { frequency?: infer F }
      ? F
      : unknown
    : unknown,
  rawDays: number | undefined
) {
  if (typeof freqRaw === "string") return freqRaw;
  if (typeof freqRaw === "number") return DAYS_TO_PRESET[freqRaw] || "other";
  if (rawDays !== undefined) return DAYS_TO_PRESET[rawDays] || "other";
  return "weekly";
}

export function normalizeCadence(
  sc?: Location["serviceCadence"]
): NonNullable<Location["serviceCadence"]> {
  const rawDays = parseDaysBetween(sc?.daysBetween);
  const frequency = resolveFrequency(sc?.frequency, rawDays);
  const hasCustom = Object.prototype.hasOwnProperty.call(sc || {}, "custom");

  const custom =
    frequency === "other"
      ? hasCustom
        ? String(sc?.custom ?? "")
        : rawDays
          ? String(rawDays)
          : ""
      : "";

  const daysBetween =
    frequency === "other"
      ? (() => {
          const customNumber = parseDaysBetween(custom);
          if (customNumber && customNumber > 0) return customNumber;
          if (hasCustom) return undefined;
          if (typeof sc?.frequency === "number") return sc.frequency;
          return rawDays;
        })()
      : PRESET_TO_DAYS[frequency];

  const days = sortDaysByWeek(sc?.days || []);

  return {
    frequency,
    custom,
    days,
    machineThresholdScope: sc?.machineThresholdScope ?? "entire_location",
    revenueThreshold: sc?.revenueThreshold ?? undefined,
    cashThreshold: sc?.cashThreshold ?? undefined,
    cardThreshold: sc?.cardThreshold ?? undefined,
    inventoryTrigger: sc?.inventoryTrigger ?? undefined,
    daysBetween: daysBetween ?? undefined,
  } as NonNullable<Location["serviceCadence"]>;
}

export function getCadenceActiveDays(
  cadence: NonNullable<Location["serviceCadence"]> | undefined,
  allDays: string[]
): string[] {
  if (!cadence) return [];
  if (cadence.frequency === "daily") return allDays;
  return cadence.days || [];
}

export function buildCadencePatchFromDays(
  frequencyDays: number,
  day: string,
): Partial<NonNullable<Location["serviceCadence"]>> {
  const frequency = resolveFrequency(undefined, frequencyDays);
  return {
    frequency: frequency as NonNullable<Location["serviceCadence"]>["frequency"],
    custom: frequency === "other" ? String(frequencyDays) : "",
    days: frequency === "daily" ? DAYS : [day],
  };
}

export function isCadenceApplied(
  cadence: NonNullable<Location["serviceCadence"]> | undefined,
  frequencyDays: number,
  day: string,
): boolean {
  const normalized = normalizeCadence(cadence);
  if (normalized.daysBetween !== frequencyDays) return false;
  const activeDays = getCadenceActiveDays(normalized, DAYS);
  if (frequencyDays === 1) return activeDays.length === DAYS.length;
  return activeDays.length === 1 && activeDays[0] === day;
}

export function applyCadenceSuggestion(
  location: Location,
  suggestion: {
    cadence: { frequency: number; dayOfWeek: string };
    suggestedAssignee?: { userId: string } | null;
  },
): Location {
  const cadencePatch = buildCadencePatchFromDays(
    suggestion.cadence.frequency,
    suggestion.cadence.dayOfWeek,
  );
  return {
    ...location,
    serviceCadence: normalizeCadence({
      ...(location.serviceCadence || {}),
      ...cadencePatch,
    }),
    defaultAssigneeId:
      suggestion.suggestedAssignee?.userId || location.defaultAssigneeId,
  };
}

export function isCadenceSuggestionApplied(
  location: Location,
  suggestion: {
    cadence: { frequency: number; dayOfWeek: string };
    suggestedAssignee?: { userId: string } | null;
  },
): boolean {
  if (
    !isCadenceApplied(
      location.serviceCadence,
      suggestion.cadence.frequency,
      suggestion.cadence.dayOfWeek,
    )
  ) {
    return false;
  }
  const suggestedAssigneeId = suggestion.suggestedAssignee?.userId;
  if (!suggestedAssigneeId) return true;
  return normalizeId(location.defaultAssigneeId) === normalizeId(suggestedAssigneeId);
}

const normalizeId = (value?: string | null) =>
  value ? value.toLowerCase().replaceAll("-", "") : "";

const DAY_ALIASES: Record<string, string[]> = {
  Monday: ["mon"],
  Tuesday: ["tue", "tues"],
  Wednesday: ["wed", "weds"],
  Thursday: ["thu", "thur", "thurs"],
  Friday: ["fri"],
  Saturday: ["sat"],
  Sunday: ["sun"],
};

const buildDayTokens = (days: string[]) =>
  days.flatMap((day) => {
    const base = day.trim();
    if (!base) return [];
    return [base, ...(DAY_ALIASES[base] || [])];
  });

export function buildCadenceSearchText(
  sc?: Location["serviceCadence"]
): string {
  if (!sc) return "";
  const normalized = normalizeCadence(sc);
  const tokens = new Set<string>();
  const days = getCadenceActiveDays(normalized, DAYS);

  buildDayTokens(days).forEach((token) => tokens.add(token));

  if (normalized.frequency) tokens.add(String(normalized.frequency));
  if (normalized.daysBetween) {
    const daysBetween = normalized.daysBetween;
    tokens.add(String(daysBetween));
    tokens.add(`every ${daysBetween} days`);
    if (daysBetween === 1) tokens.add("daily");
    if (daysBetween === 7) tokens.add("weekly");
    if (daysBetween === 14) {
      tokens.add("biweekly");
      tokens.add("bi-weekly");
      tokens.add("bimonthly");
    }
    if (daysBetween === 30) tokens.add("monthly");
  }

  return Array.from(tokens).join(" ");
}

export function buildCadenceSearchLookup(
  locations: ({ id?: string; serviceCadence?: Location["serviceCadence"] } | null | undefined)[]
): Record<string, string> {
  const map: Record<string, string> = {};
  locations.forEach((loc) => {
    if (loc?.id) map[loc.id] = buildCadenceSearchText(loc.serviceCadence);
  });
  return map;
}
